/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useEffect, useId, useState } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { readMsrpVisibleFromCookie } from '@/lib/msrp';
import { resourceRoutes } from '@/route-paths';

/**
 * MSRP price-visibility toggle for the header utility bar (CRCB2B-114, header half).
 *
 * **PRESENTATION ONLY.** The toggle persists a preference that nothing reads, so
 * flipping it changes no displayed price. The price-display half is CRCB2B-114.
 * This is the agreed seam, not a defect — see `lib/msrp` before building on it.
 *
 * Renders switch-then-label, matching Figma's banner.
 *
 * SSR renders the switch off and `useEffect` reconciles the cookie after mount;
 * reading it during render would cause a hydration mismatch or force a loader onto
 * a header that has none. `fetcher.formData` covers the in-flight window.
 */
export default function MsrpToggle(): ReactElement {
    const id = useId();
    const { t } = useTranslation('header');
    const fetcher = useFetcher();

    // SSR renders off; the persisted value arrives after mount (no mismatch).
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        setVisible(readMsrpVisibleFromCookie());
    }, []);

    // Optimistic: an in-flight submission wins over local state.
    const checked = fetcher.formData ? fetcher.formData.get('visible') === 'true' : visible;

    const handleChange = (next: boolean) => {
        setVisible(next);
        const formData = new FormData();
        formData.set('visible', String(next));
        void fetcher.submit(formData, { method: 'POST', action: resourceRoutes.setMsrpPreference });
    };

    return (
        <div className="flex items-center gap-2">
            <Switch id={id} checked={checked} onCheckedChange={handleChange} aria-label={t('msrp.ariaLabel')} />
            <Label htmlFor={id} className="cursor-pointer">
                {t('msrp.label')}
            </Label>
        </div>
    );
}
